import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, RefreshCw, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';

const HexToRgbTool = () => {
  const [hex, setHex] = useState('#3b82f6');
  const [rgb, setRgb] = useState({ r: 59, g: 130, b: 246 });

  const hexToRgb = (h) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r, g, b) => {
    return '#' + [r, g, b].map(x => {
      const hex = Math.max(0, Math.min(255, x)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  };

  const handleHexChange = (value) => {
    setHex(value);
    const result = hexToRgb(value);
    if (result) setRgb(result);
  };

  const handleRgbChange = (key, value) => {
    const newRgb = { ...rgb, [key]: parseInt(value) || 0 };
    setRgb(newRgb);
    setHex(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
  };

  const copyHex = () => {
    navigator.clipboard.writeText(hex);
    toast.success('HEX copied!');
  };

  const copyRgb = () => {
    navigator.clipboard.writeText(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
    toast.success('RGB copied!');
  };

  return (
    <div className="space-y-6" data-testid="hex-rgb-tool">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">HEX Color</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={hex}
                onChange={(e) => handleHexChange(e.target.value)}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <Input
                value={hex}
                onChange={(e) => handleHexChange(e.target.value)}
                placeholder="#000000"
                className="font-mono"
              />
              <Button onClick={copyHex} variant="outline" size="icon">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-center py-4">
            <ArrowRightLeft className="w-6 h-6 text-muted-foreground" />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">RGB Values</label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">R</label>
                <Input
                  type="number"
                  min={0}
                  max={255}
                  value={rgb.r}
                  onChange={(e) => handleRgbChange('r', e.target.value)}
                  className="font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">G</label>
                <Input
                  type="number"
                  min={0}
                  max={255}
                  value={rgb.g}
                  onChange={(e) => handleRgbChange('g', e.target.value)}
                  className="font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">B</label>
                <Input
                  type="number"
                  min={0}
                  max={255}
                  value={rgb.b}
                  onChange={(e) => handleRgbChange('b', e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>
            <Button onClick={copyRgb} variant="outline" className="w-full mt-2">
              <Copy className="w-4 h-4 mr-2" /> Copy rgb({rgb.r}, {rgb.g}, {rgb.b})
            </Button>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center">
          <div
            className="w-full aspect-square max-w-[200px] rounded-xl border-4 border-border shadow-lg"
            style={{ backgroundColor: hex }}
          />
          <div className="mt-4 text-center">
            <p className="font-mono text-lg">{hex}</p>
            <p className="font-mono text-sm text-muted-foreground">rgb({rgb.r}, {rgb.g}, {rgb.b})</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-center">
        <Button onClick={() => { setHex('#000000'); setRgb({ r: 0, g: 0, b: 0 }); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>
    </div>
  );
};

export default HexToRgbTool;
